import React from 'react';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { PrintDataFields, getPrintHeaderVariant, PDFBevaEstimateBlock } from '@repo/print-pdf/arbre/print';
import {
  pdfStyles,
  PDFHeader,
  PDFInfoField,
  PDFSectionHeader,
  PDFRiskIndicator,
  normalizeRiskLevel,
  convertImageUrlForPDF,
} from '@repo/print-pdf/shared';

interface PDFDocumentProps {
  data: PrintDataFields;
  inventaireId?: number;
}

export const PDFDocument: React.FC<PDFDocumentProps> = ({
  data,
  inventaireId,
}) => {
  const headerVariant = getPrintHeaderVariant(data.inventaireType);

  // Format BEVA value
  const formatBeva = () => {
    if (data.beva === '—' || !data.beva) return '—';
    const bevaNum = typeof data.beva === 'number'
      ? data.beva
      : parseFloat(String(data.beva));
    if (isNaN(bevaNum)) return `${data.beva} €`;
    return `${data.beva} €`;
  };



  const getCompleteTreeImageUrl = (houppierLevel: string, troncLevel: string, colletLevel: string): string => {
    // Build complete image path: {houppier}_{tronc}_{collet}_Complete.png
    const imagePath = `/images/Tree/complete/${houppierLevel}_${troncLevel}_${colletLevel}_Complete.png`;
    // Use absolute URL for react-pdf to load images correctly
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      const host = window.location.host;
      return `${protocol}//${host}${imagePath}`;
    }
    // Fallback for SSR
    return imagePath;
  };

  return (

    <>
      {/* Page 1 */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Header Section */}
        <PDFHeader
          organizationName={data.organizationName}
          organizationAddress={data.organizationAddress}
          organizationCity={data.organizationCity}
          organizationZipCode={data.organizationZipCode}
          organizationSiret={data.organizationSiret}
          organizationLogo={data.organizationLogo}
          user={data.user}
          logoSize="default"
        />

        {/* Main Title */}
        <PDFSectionHeader
          title={`FICHE DE SYNTHESE ARBRE - ${inventaireId}`}
          variant={headerVariant}
        />

        {/* Basic Information */}
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Date" value={data.formattedDate} />
            <PDFInfoField label="Genre (latin)" value={data.genreLatin} />
            <PDFInfoField label="Nom (FR)" value={data.especeLabel} />
            <PDFInfoField label="Adresse" value={data.addressLine} />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Code site" value={data.code_site} />
            <PDFInfoField label="Espèce" value={data.especeLatin} />
            <PDFInfoField label="Cultivar" value={data.cultivar} />
          </View>
          <View style={pdfStyles.columnNoLeft}>
            <PDFInfoField label="Identification" value={data.num_sujet} />
            <PDFInfoField label="Coordonnées GPS" value={data.gps} />
          </View>
        </View>

        {/* Caractéristiques */}
        <PDFSectionHeader title="CARACTÉRISTIQUES" variant="secondary" />
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Hauteur en m" value={data.hauteur} />
            <PDFInfoField label="Diamètre en cm" value={data.diametre} />
            <PDFInfoField label="Âge estimé" value={data.age_estime} />
            <PDFInfoField label="Stade de dév." value={data.caracteristiques.stade_dev} />
            <PDFInfoField label="Remarquable" value={data.caracteristiques.critere} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Port" value={data.caracteristiques.port_arbre} />
            <PDFInfoField label="Tronc" value={data.caracteristiques.caract_tronc} />
            <PDFInfoField label="Pied de l'arbre" value={data.caracteristiques.caract_pied} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Implantation" value={data.caracteristiques.implantation} />
            <PDFInfoField label="Domaine" value={data.caracteristiques.domaine} />
            <PDFInfoField label="Situation" value={data.caracteristiques.situation} />
            <PDFInfoField label="Accès" value={data.caracteristiques.accessibilite} />
          </View>
        </View>

        {/* Environnement */}
        <PDFSectionHeader title="ENVIRONNEMENT" variant="secondary" />
        <View style={pdfStyles.grid3}>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Cohabitation" value={data.environnement.proximite} />
            <PDFInfoField label="Demande de DICT" value={data.environnement.dictLabel} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Nuisance" value={data.environnement.nuisance} />
          </View>
          <View style={pdfStyles.column}>
            <PDFInfoField label="Fréquentation" value={data.environnement.taux_freq} />
            <PDFInfoField label="Type de Passage" value={data.environnement.type_passage} />
          </View>
        </View>

        {/* Diagnostic Visuel */}
        <PDFSectionHeader title="DIAGNOSTIC VISUEL" variant="secondary" />
        <View style={{ marginTop: 4, marginBottom: 6, width: '100%' }}>
          <PDFBevaEstimateBlock
            formattedBeva={formatBeva()}
            bevaDetail={data.bevaDetail}
          />
        </View>
        <View style={{ paddingVertical: 10, display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <View>
            <View style={pdfStyles.riskEvaluation}>
              <Text style={pdfStyles.riskEvalTitle}>Évaluation du risque</Text>
              <View style={pdfStyles.riskBar}>
                <View style={pdfStyles.riskBarItemContainer}>
                  <View style={[pdfStyles.riskBarItem, pdfStyles.riskLow]} />
                  <Text style={pdfStyles.riskLabel}> Risque faible</Text>
                </View>
                <View style={pdfStyles.riskBarItemContainer}>
                  <View style={[pdfStyles.riskBarItem, pdfStyles.riskModerate]} />
                  <Text style={pdfStyles.riskLabel}> Risque modéré</Text>
                </View>
                <View style={pdfStyles.riskBarItemContainer}>
                  <View style={[pdfStyles.riskBarItem, pdfStyles.riskHigh]} />
                  <Text style={pdfStyles.riskLabel}> Risque élevé</Text>
                </View>
                <View style={pdfStyles.riskBarItemContainer}>
                  <View style={[pdfStyles.riskBarItem, pdfStyles.riskImminent]} />
                  <Text style={pdfStyles.riskLabel}> Risque imminent</Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Photos with Tree Risk Visualization - 4 columns */}
        <View style={[pdfStyles.grid4, { marginTop: 5 }]}>
          {/* Tree Risk Visualization Column */}
          <View style={pdfStyles.columnNoLeft}>
            <View style={pdfStyles.photoContainer}>
              <Image
                src={getCompleteTreeImageUrl(
                  normalizeRiskLevel(data.risque.houppier),
                  normalizeRiskLevel(data.risque.tronc),
                  normalizeRiskLevel(data.risque.collet)
                )}
                style={pdfStyles.photo}
              />
            </View>
          </View>
          {data.img1 && (
            <View style={pdfStyles.columnNoLeft}>
              <View style={pdfStyles.photoContainer}>
                <Image
                  src={convertImageUrlForPDF(data.img1, { width: 700 })}
                  style={pdfStyles.photo}
                />
              </View>
            </View>
          )}
          {data.img2 && (
            <View style={pdfStyles.columnNoLeft}>
              <View style={pdfStyles.photoContainer}>
                <Image
                  src={convertImageUrlForPDF(data.img2, { width: 700 })}
                  style={pdfStyles.photo}
                />
              </View>
            </View>
          )}
          {data.img3 && (
            <View style={pdfStyles.columnNoLeft}>
              <View style={pdfStyles.photoContainer}>
                <Image
                  src={convertImageUrlForPDF(data.img3, { width: 700 })}
                  style={pdfStyles.photo}
                />
              </View>
            </View>
          )}
        </View>
      </Page>

      {/* Page 2 - Diagnostic Sanitaire */}
      <Page size="A4" style={pdfStyles.page}>
        {/* Diagnostic sanitaire */}
        <PDFSectionHeader title="Diagnostic sanitaire" variant="secondary" />

        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeader}>
            <Text style={[pdfStyles.tableHeaderCell, { flex: 1 }]}></Text>
            <Text style={[pdfStyles.tableHeaderCell, { flex: 2 }]}>Défauts mécaniques</Text>
            <Text style={[pdfStyles.tableHeaderCell, { flex: 2 }]}>Préconisations de travaux</Text>
            <Text style={[pdfStyles.tableHeaderCell, { flex: 1, borderRight: 'none' }]}>Risque de rupture</Text>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={[pdfStyles.tableCell, { flex: 1, fontWeight: 'bold', fontSize: 8, color: '#4E5155' }]}>Système racinaire</Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.defautsMecaniques.collet}
            </Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.travaux.travaux_collet}
            </Text>
            <View style={[pdfStyles.tableCellLast, { flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: 8 }]}>
              <PDFRiskIndicator risk={data.risque.collet} showLabel={true} size="default" />
            </View>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={[pdfStyles.tableCell, { flex: 1, fontWeight: 'bold', fontSize: 8, color: '#4E5155' }]}>Tronc</Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.defautsMecaniques.tronc}
            </Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.travaux.travaux_tronc}
            </Text>
            <View style={[pdfStyles.tableCellLast, { flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: 8 }]}>
              <PDFRiskIndicator risk={data.risque.tronc} showLabel={true} size="default" />
            </View>
          </View>
          <View style={pdfStyles.tableRow}>
            <Text style={[pdfStyles.tableCell, { flex: 1, fontWeight: 'bold', fontSize: 8, color: '#4E5155' }]}>Houppier</Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.defautsMecaniques.houppier}
            </Text>
            <Text style={[pdfStyles.tableCell, { flex: 2, fontSize: 8, padding: 4 }]}>
              {data.travaux.travaux_houppier}
            </Text>
            <View style={[pdfStyles.tableCellLast, { flex: 1, flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: 8 }]}>
              <PDFRiskIndicator risk={data.risque.houppier} showLabel={true} size="default" />
            </View>
          </View>
        </View>

        {/* État sanitaire global and Date prochaine visite */}
        <View style={pdfStyles.twoColGrid}>
          <View style={{ flex: 1 }}>
            <View style={pdfStyles.stateTable}>
              <View style={{ flexDirection: 'row', border: '1pt solid #9ca3af' }}>
                <View style={{ flex: 1, width: 50 }}>
                  <Text style={[pdfStyles.stateTableCellBold]}>Etat sanitaire global</Text>
                </View>
                <View style={{ flex: 1, width: 100 }}>
                  <View style={[pdfStyles.stateTableCellValue, { width: '100%' }]}>
                    <Text style={{ fontSize: 8, textTransform: 'capitalize', flexWrap: 'wrap' }}>
                      {data.etatSanitaire.general?.trim()}
                    </Text>
                  </View>
                </View>
              </View>
              <View style={{ flexDirection: 'row', border: '1pt solid #9ca3af', borderTop: 'none' }}>
                <View style={{ flex: 1 }}>
                  <Text style={[pdfStyles.stateTableCellBold, { borderBottom: 'none' }]}>Risque de rupture général</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <View style={[pdfStyles.stateTableCellValueLast, { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 5 }]}>
                    <PDFRiskIndicator risk={data.risque.general} showLabel={true} size="default" />
                  </View>
                </View>
              </View>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <View style={[pdfStyles.stateTable, { border: '1pt solid #9ca3af', borderBottom: 'none', borderLeft: 'none', borderRight: 'none' }]}>
              <View style={{ flexDirection: 'row', border: '1pt solid #9ca3af', borderTop: 'none' }}>
                <View style={{ flex: 1 }}>
                  <Text style={pdfStyles.stateTableCellBold}>Date d'intervention</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={pdfStyles.stateTableCellValue}>
                    {data.travaux.date_travaux}
                  </Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', border: '1pt solid #9ca3af' }}>
                <View style={{ flex: 1 }}>
                  <Text style={pdfStyles.stateTableCellBold}>Date de prochaine visite</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={pdfStyles.stateTableCellValue}>
                    {data.travaux.date_pro_visite}
                  </Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row', border: '1pt solid #9ca3af', borderTop: 'none' }}>
                <View style={{ flex: 1 }}>
                  <Text style={[pdfStyles.stateTableCellBold, { borderBottom: 'none' }]}>DT/DICT requis</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={pdfStyles.stateTableCellValueLast}>
                    {data.dictRequired}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Commentaires */}
        <View style={pdfStyles.commentSection}>
          <Text style={pdfStyles.label}>Commentaires :</Text>
          <Text style={{ fontSize: 9, color: '#4b5563', marginTop: 5 }}>
            {data.comments}
          </Text>
        </View>
      </Page>
    </>
  );
};
